var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useReducer, useCallback, useRef, memo, forwardRef, useEffect } from 'react';
import { useMemo } from 'react';
import { mergeConfig } from 'vega';
import { Vega } from 'react-vega';
import { useTheme, useForkRef } from '@material-ui/core';
import { createTheme } from '@material-vega/core';
import { defaultVegaTooltipOptions } from './MuiVegaTheme';
import { calculatePosition } from './tooltip/position';
import { MuiVegaTooltip } from './tooltip/MuiVegaTooltip';
import { useMuiVegaOptions } from './useMuiVegaTheme';
var VegaMemo = memo(Vega);
/**
 * <Vega> from react-vega but with a Material UI integrated theme
 */
export var MuiVega = forwardRef(function (props, parentRef) {
    var variant = props.variant, _a = props.color, color = _a === void 0 ? 'primary' : _a, _b = props.background, background = _b === void 0 ? 'transparent' : _b, _c = props.autoResize, autoResize = _c === void 0 ? false : _c, tooltipProp = props.tooltip, _d = props.TooltipComponent, TooltipComponent = _d === void 0 ? MuiVegaTooltip : _d, configProp = props.config, themeOptions = props.themeOptions, vegaProps = __rest(props, ["variant", "color", "background", "autoResize", "tooltip", "TooltipComponent", "config", "themeOptions"]);
    var theme = useTheme();
    var _e = __assign(__assign({}, defaultVegaTooltipOptions), theme.vegaTooltip), offsetX = _e.offsetX, offsetY = _e.offsetY;
    if (typeof configProp === 'string') {
        throw new TypeError('@material-vega/material-ui does not support a string `config`.');
    }
    var vegaRef = useRef();
    var ref = useForkRef(vegaRef, parentRef);
    useEffect(function () {
        var vegaEmbed = vegaRef.current.vegaEmbed.current;
        var container = vegaEmbed.containerRef.current;
        if (autoResize) {
            var ro_1 = new ResizeObserver(function () {
                // Vega does not have a way to tell it that the container has resized
                // So we have to fake a window resize event when the container resizes
                window.dispatchEvent(new Event('resize'));
            });
            ro_1.observe(container);
            return function () {
                ro_1.disconnect();
            };
        }
    }, []);
    var _f = useReducer(function (state, _a) {
        var event = _a.event, value = _a.value;
        if (value == null || value === '')
            return null;
        var container = vegaRef.current.vegaEmbed.current.containerRef.current;
        var _b = calculatePosition(event, container.getBoundingClientRect(), offsetX, offsetY), x = _b.x, y = _b.y;
        return { position: { x: x, y: y }, value: value };
    }, undefined), tooltipState = _f[0], dispatchTooltip = _f[1];
    var unmounted = useRef(false);
    useEffect(function () {
        unmounted.current = false;
        return function () {
            unmounted.current = true;
        };
    }, []);
    var tooltipHandler = useCallback(function (handler, event, item, value) {
        if (!unmounted.current) {
            dispatchTooltip({ handler: handler, event: event, item: item, value: value });
        }
    }, []);
    var tooltip = tooltipProp || tooltipHandler;
    var muiThemeOptions = useMuiVegaOptions({ color: color, background: background });
    var config = useMemo(function () {
        return mergeConfig(createTheme(__assign(__assign({}, muiThemeOptions), themeOptions), variant), configProp || {});
    }, [theme, color, themeOptions]);
    return (React.createElement(React.Fragment, null,
        React.createElement(VegaMemo, __assign({ ref: ref, config: config, tooltip: tooltip, renderer: "svg" }, vegaProps)),
        tooltipState !== undefined && (React.createElement(TooltipComponent, __assign({ value: null }, tooltipState)))));
});
MuiVega.displayName = 'MuiVega';
